import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, MapPin, Calendar, CreditCard, Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";

const STATUS_LABEL = {
  new: { label: "Quote requested", tone: "bg-[#0B1215] text-[#F8F9FA]" },
  contacted: { label: "Slot confirmed", tone: "bg-[#FF5A36] text-[#F8F9FA]" },
  completed: { label: "Completed", tone: "bg-[#16a34a] text-[#F8F9FA]" },
};

export default function CustomerJobPortal() {
  const { token } = useParams();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/booking-view/${token}`).then((r) => {
      setB(r.data);
      setLoading(false);
    }).catch(() => {
      setErr(true);
      setLoading(false);
    });
  }, [token]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 size={32} className="animate-spin text-[#0B1215]" />
        </div>
      </Layout>
    );
  }

  if (err || !b) {
    return (
      <Layout>
        <section className="min-h-[60vh] flex items-center justify-center px-6 py-24 text-center">
          <div>
            <div className="font-mono-label text-[#FF5A36] mb-3">· NOT FOUND</div>
            <h1 className="font-display text-4xl tracking-tighter mb-4">Job link not recognised.</h1>
            <p className="text-[#495057]">Check the URL or contact Rob on 07346 812 685.</p>
            <Link to="/" className="mt-8 inline-block font-mono-label text-[#FF5A36]">← Back to home</Link>
          </div>
        </section>
      </Layout>
    );
  }

  const statusBadge = STATUS_LABEL[b.status] || STATUS_LABEL.new;

  const invoiceCardClass = (() => {
    if (b.invoice_status === "paid") return "border-[#16a34a] bg-[#16a34a]/10";
    if (b.invoice_status === "sent") return "border-[#FF5A36] bg-[#FF5A36]/10";
    return "border-dashed border-[#0B1215]/30 bg-[#F8F9FA]";
  })();

  const renderInvoiceBody = () => {
    if (b.invoice_status === "paid") {
      return (
        <>
          <div className="flex items-center gap-2 mb-2"><CheckCircle2 size={18} className="text-[#16a34a]"/><span className="font-mono-label text-[#16a34a]">PAID</span></div>
          <div className="font-display text-2xl tracking-tighter">£{b.invoice_amount?.toFixed(2)}</div>
        </>
      );
    }
    if (b.invoice_status === "sent") {
      return (
        <>
          <div className="font-display text-3xl tracking-tighter mb-3">£{b.invoice_amount?.toFixed(2)}</div>
          <a href={b.invoice_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#FF5A36] hover:bg-[#E04928] text-[#F8F9FA] px-5 py-3 font-mono-label" data-testid="pay-now-btn">
            PAY NOW →
          </a>
        </>
      );
    }
    return (
      <div className="text-[#495057] text-sm">
        {b.estimated_price ? (<>Auto-estimate <strong className="text-[#FF5A36]">from £{b.estimated_price}</strong>. Final fixed invoice arrives here once Rob's confirmed the job.</>) : "Final fixed quote will appear here within 24 hours."}
      </div>
    );
  };

  return (
    <Layout>
      <section className="py-12 sm:py-16 border-b border-[#0B1215]/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="font-mono-label text-[#FF5A36] mb-4">· YOUR JOB</div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h1 className="font-display text-4xl sm:text-5xl tracking-tighter">
              Hi {b.name.split(" ")[0]}.
            </h1>
            <span className={`font-mono-label px-4 py-2 ${statusBadge.tone}`} data-testid="job-status">
              · {statusBadge.label.toUpperCase()}
            </span>
          </div>
          <p className="mt-4 text-[#495057] max-w-2xl">
            Everything happening with your <strong>{b.service}</strong> sits here. Bookmark this page —
            you'll see slot confirmations, before/after photos, progress updates and your invoice as
            things move along.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Job details */}
          <div className="border-2 border-[#0B1215] bg-[#F8F9FA] p-6">
            <div className="font-mono-label text-[#FF5A36] mb-3">· DETAILS</div>
            <dl className="space-y-3 text-sm">
              <div><dt className="font-mono-label text-[#495057]">Service</dt><dd className="font-semibold">{b.service}</dd></div>
              {b.property_type && <div><dt className="font-mono-label text-[#495057]">Property</dt><dd>{b.property_type}</dd></div>}
              {b.address && <div><dt className="font-mono-label text-[#495057]">Address</dt><dd>{b.address}</dd></div>}
              {b.preferred_date && <div><dt className="font-mono-label text-[#495057]">Preferred date</dt><dd>{b.preferred_date}</dd></div>}
            </dl>
          </div>

          {/* Confirmed slot */}
          <div className={`border-2 p-6 ${b.confirmed_slot ? "border-[#FF5A36] bg-[#FF5A36]/10" : "border-dashed border-[#0B1215]/30 bg-[#F8F9FA]"}`}>
            <div className="font-mono-label text-[#FF5A36] mb-3 flex items-center gap-2"><Calendar size={14}/> · BOOKED SLOT</div>
            {b.confirmed_slot ? (
              <div className="font-display text-2xl sm:text-3xl tracking-tighter leading-tight" data-testid="confirmed-slot">{b.confirmed_slot}</div>
            ) : (
              <div className="text-[#495057] text-sm leading-relaxed">Waiting for Rob to confirm. You'll get an email the moment it's locked in.</div>
            )}
          </div>

          {/* Invoice */}
          <div className={`border-2 p-6 ${invoiceCardClass}`}>
            <div className="font-mono-label text-[#FF5A36] mb-3 flex items-center gap-2"><CreditCard size={14}/> · INVOICE</div>
            {renderInvoiceBody()}
          </div>
        </div>
      </section>

      {/* Photos */}
      {b.job_photos?.length > 0 && (
        <section className="py-12 bg-[#0B1215] text-[#F8F9FA] grain relative">
          <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="font-mono-label text-[#FF5A36] mb-6">· JOB PHOTOS · {b.job_photos.length}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="job-photos">
              {b.job_photos.map((url, i) => (
                <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="block aspect-square overflow-hidden bg-[#F8F9FA]/5">
                  <img src={url} alt={`Job photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy"/>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Progress timeline */}
      <section className="py-12 sm:py-16 bg-[#E9ECEF] border-t border-[#0B1215]/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="font-mono-label text-[#FF5A36] mb-6 flex items-center gap-2"><Clock size={14}/> · PROGRESS</div>
          {b.progress_updates?.length > 0 ? (
            <ol className="space-y-4" data-testid="progress-timeline">
              {b.progress_updates.slice().reverse().map((u, i) => (
                <li key={u.id || i} className="border-2 border-[#0B1215] bg-[#F8F9FA] p-5 flex gap-4">
                  <div className="font-mono-label text-[#FF5A36] shrink-0">{new Date(u.at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</div>
                  <div>
                    <p className="text-sm leading-relaxed">{u.note}</p>
                    {u.photo_url && <a href={u.photo_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-xs font-mono-label text-[#FF5A36] hover:underline">View attached photo →</a>}
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-[#495057] text-sm border-2 border-dashed border-[#0B1215]/30 p-6 bg-[#F8F9FA]">
              No updates yet. Rob will post progress notes here as the job moves along.
            </p>
          )}
        </div>
      </section>

      <section className="py-10 text-center text-sm text-[#495057]">
        <MapPin size={14} className="inline -mt-0.5 mr-1"/>Got a question? Call Rob on <a href="tel:+447346812685" className="font-bold text-[#0B1215]">07346 812 685</a>
      </section>
    </Layout>
  );
}
